import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tabs, Button, Tag, Modal, Spin, Empty, Segmented } from 'antd';
import { ReloadOutlined, CalculatorOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { userRequest } from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeError, NdoePeriodPicker, NdoeStatCard, NdoeStats,
} from '../../components/ndoe';
import './analyseDepenses.css';

/*
  Analyse des coûts de trajet — écran admin (permission depenses.analyser).

  Lit les feuilles VALIDÉES. Répond à la question du corpus : le coût moyen par
  mission a monté de +44 % en trois ans à tarif unitaire constant — donc ce sont
  les étapes qui se multiplient. Cet écran rend la dérive mesurable, par trajet,
  par livreur, par moyen, et attache enfin un coût de transport à chaque commande.

  Piège du dépôt : aucun sous-composant défini dans le rendu.
*/

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

// Convertit la sélection du NdoePeriodPicker en bornes AAAA-MM-JJ pour l'API.
// Sans dayjs (Date simple) : cohérent avec le reste du dépôt.
const jourStr = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const bornesDe = (periode) => {
  if (periode.period === 'custom') {
    return { debut: periode.start_date || null, fin: periode.end_date || null };
  }
  const fin = new Date();
  const debut = new Date();
  debut.setDate(debut.getDate() - (Number(periode.period) || 30));
  return { debut: jourStr(debut), fin: jourStr(fin) };
};

// Étiquette de dérive : le coût réel a-t-il glissé au-dessus de la référence ?
const deriveTag = (t) => {
  if (t.derive === null || t.ref_median === null) return <Tag>sans référence</Tag>;
  if (t.derive > 0) return <Tag color="warning">+{fmt(t.derive)} FC</Tag>;
  if (t.derive < 0) return <Tag color="success">{fmt(t.derive)} FC</Tag>;
  return <Tag color="default">conforme</Tag>;
};

const AnalyseDepenses = () => {
  const [periode, setPeriode] = useState({ period: 90 });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recalc, setRecalc] = useState(false);

  const [synthese, setSynthese] = useState(null);
  const [trajets, setTrajets] = useState([]);
  const [commandes, setCommandes] = useState([]);

  const bornes = useMemo(() => bornesDe(periode), [periode]);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const params = {};
      if (bornes.debut) params.debut = bornes.debut;
      if (bornes.fin) params.fin = bornes.fin;
      const [s, t, c] = await Promise.all([
        userRequest.get('/api/feuille-depense/analyse/synthese', { params }),
        userRequest.get('/api/feuille-depense/analyse/trajets', { params: { ...params, min: 2 } }),
        userRequest.get('/api/feuille-depense/analyse/commandes', { params }),
      ]);
      setSynthese(s.data);
      setTrajets(t.data?.trajets || []);
      setCommandes(c.data?.commandes || []);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Analyse indisponible.');
    } finally {
      setChargement(false);
    }
  }, [bornes.debut, bornes.fin]);

  useEffect(() => { charger(); }, [charger]);

  const [seuil, setSeuil] = useState(5);
  const recalculer = () => Modal.confirm({
    title: 'Recaler les tarifs de référence ?',
    content: `Les tarifs des couples vus au moins ${seuil} fois sur la période seront recalculés sur les feuilles validées. Les autres gardent leur valeur actuelle.`,
    okText: 'Recalculer', cancelText: 'Annuler',
    onOk: async () => {
      setRecalc(true);
      try {
        const params = {};
        if (bornes.debut) params.debut = bornes.debut;
        if (bornes.fin) params.fin = bornes.fin;
        const { data } = await userRequest.post('/api/feuille-depense/analyse/recalculer-tarifs', { min: seuil, ...params });
        toast.success(`${data.couples_recalcules} tarif(s) recalé(s) sur la réalité.`);
        await charger();
      } catch (e) {
        toast.error(e.response?.data?.error || 'Recalcul impossible.');
      } finally {
        setRecalc(false);
      }
    },
  });

  if (erreur) return <NdoePage><NdoeError text={erreur} onRetry={charger} /></NdoePage>;

  const parLivreur = synthese?.par_livreur || [];
  const parMoyen = synthese?.par_moyen || [];

  return (
    <NdoePage className="analyse-depenses">
      <NdoePageHeader
        eyebrow="Dépenses"
        title="Coûts de trajet"
        subtitle="Ce que coûtent réellement les livraisons, d’après les feuilles validées."
        actions={<Button icon={<ReloadOutlined />} onClick={charger}>Rafraîchir</Button>}
      />

      <NdoePeriodPicker value={periode} onChange={setPeriode} />

      {chargement ? (
        <div className="ad-centre"><Spin size="large" /></div>
      ) : (
        <>
          <NdoeStats>
            <NdoeStatCard label="Total transport" value={`${fmt(synthese?.total_fc)} FC`} gold />
            <NdoeStatCard label="Feuilles validées" value={fmt(synthese?.nb_feuilles)} />
            <NdoeStatCard label="Trajets & postes" value={fmt(synthese?.nb_lignes)} />
            <NdoeStatCard
              label="Moyenne / trajet"
              value={`${synthese?.nb_lignes ? fmt(Math.round(synthese.total_fc / synthese.nb_lignes)) : 0} FC`}
            />
          </NdoeStats>

          <Tabs
            defaultActiveKey="trajets"
            items={[
              {
                key: 'trajets',
                label: `Trajets (${trajets.length})`,
                children: trajets.length === 0 ? <Empty description="Pas assez de trajets validés sur la période." /> : (
                  <div className="ad-tw">
                    <table className="ad-table">
                      <thead>
                        <tr>
                          <th>Trajet</th><th className="n">Occur.</th><th className="n">Médian</th>
                          <th className="n">Min–max</th><th className="n">Référence</th><th>Dérive</th><th className="n">Cumul</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trajets.map((t) => (
                          <tr key={`${t.depart}-${t.arrivee}`}>
                            <td className="name">{t.nom_depart} → {t.nom_arrivee}</td>
                            <td className="n">{fmt(t.occurrences)}</td>
                            <td className="n"><b>{fmt(t.median)}</b></td>
                            <td className="n muted">{fmt(t.min)}–{fmt(t.max)}</td>
                            <td className="n muted">{t.ref_median != null ? `${fmt(t.ref_median)} FC` : '—'}</td>
                            <td>{deriveTag(t)}</td>
                            <td className="n">{fmt(t.total_fc)} FC</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ),
              },
              {
                key: 'livreurs',
                label: `Par livreur (${parLivreur.length})`,
                children: (
                  <div className="ad-tw">
                    <table className="ad-table">
                      <thead>
                        <tr><th>Livreur</th><th className="n">Jours</th><th className="n">Trajets</th><th className="n">Moyenne</th><th className="n">Total</th></tr>
                      </thead>
                      <tbody>
                        {parLivreur.map((l) => (
                          <tr key={l.id_user}>
                            <td className="name">{l.livreur || `#${l.id_user}`}</td>
                            <td className="n">{fmt(l.jours)}</td>
                            <td className="n">{fmt(l.trajets)}</td>
                            <td className="n muted">{fmt(l.moyenne_fc)} FC</td>
                            <td className="n"><b>{fmt(l.total_fc)} FC</b></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ),
              },
              {
                key: 'moyens',
                label: 'Par moyen',
                children: (
                  <div className="ad-tw">
                    <table className="ad-table">
                      <thead><tr><th>Moyen</th><th className="n">Trajets</th><th className="n">Moyenne</th><th className="n">Total</th></tr></thead>
                      <tbody>
                        {parMoyen.map((m) => (
                          <tr key={m.moyen}>
                            <td className="name">{m.moyen}</td>
                            <td className="n">{fmt(m.trajets)}</td>
                            <td className="n muted">{fmt(m.moyenne_fc)} FC</td>
                            <td className="n"><b>{fmt(m.total_fc)} FC</b></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ),
              },
              {
                key: 'commandes',
                label: `Coût par commande (${commandes.length})`,
                children: commandes.length === 0 ? <Empty description="Aucune livraison rattachée sur la période." /> : (
                  <>
                    <p className="ad-aide">
                      Le coût de transport imputé à chaque commande — ce que le rapport WhatsApp ne permettait pas.
                      À rapprocher de la marge pour connaître la rentabilité réelle d’une livraison.
                    </p>
                    <div className="ad-tw">
                      <table className="ad-table">
                        <thead><tr><th>Commande</th><th>Cliente</th><th className="n">Trajets</th><th className="n">Coût transport</th></tr></thead>
                        <tbody>
                          {commandes.map((c) => (
                            <tr key={c.id_commande}>
                              <td className="name">#{c.id_commande}</td>
                              <td>{c.cliente || '—'}</td>
                              <td className="n">{fmt(c.trajets)}</td>
                              <td className="n"><b>{fmt(c.cout_transport_fc)} FC</b></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ),
              },
            ]}
          />

          <div className="ad-recalc">
            <div className="ad-recalc-texte">
              <strong>Recaler les tarifs de référence</strong>
              <span>Les tarifs partent du corpus WhatsApp. Recalez-les sur les feuilles validées récentes pour qu’ils suivent la réalité.</span>
            </div>
            <div className="ad-recalc-action">
              {/* Segmented plutôt qu'InputNumber : ce dernier est un piège connu du
                  dépôt (state antd désynchronisé) et un seuil libre n'apporte rien
                  ici — 2/5/10 couvrent le besoin (couple rare / courant / fréquent). */}
              <span>Seuil d’observations&nbsp;:</span>
              <Segmented
                options={[{ label: '2', value: 2 }, { label: '5', value: 5 }, { label: '10', value: 10 }]}
                value={seuil}
                onChange={(v) => setSeuil(Number(v))}
              />
              <Button icon={<CalculatorOutlined />} loading={recalc} onClick={recalculer}>Recalculer</Button>
            </div>
          </div>
        </>
      )}
    </NdoePage>
  );
};

export default AnalyseDepenses;
